import { useMemo } from 'react';
import Box from '@mui/material/Box';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { labelToColor } from '../helpers/avatarColor';
import { bgnToEur } from '../../domain/currency';
import {
  buildAbroadChartSeries,
  buildChartSeries,
  buildCountryChartSeries,
  type PriceSnapshot,
} from '../../domain/pricing';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const LINE_COLOR = '#0f1b2d';
const FILL_COLOR = 'rgba(15, 27, 45, 0.08)';
// The theme's reserved accent color (src/theme.ts) - reused here so the always-on "Abroad"
// comparison line reads as the featured/highlighted line, distinct from the per-country palette.
const ABROAD_COLOR = '#f0a202';

const NO_GENERATIONS: string[] = [];

interface ChartProps {
  data: PriceSnapshot[];
  generation: string | null;
  /** Generations to average over when no engine is picked; empty = the whole model. */
  generations?: string[];
  countries: string[];
}

export default function Chart({ data, generation, generations = NO_GENERATIONS, countries }: ChartProps) {
  const series = useMemo(() => buildChartSeries(data, generation, generations), [data, generation, generations]);
  const abroadSeries = useMemo(() => buildAbroadChartSeries(data), [data]);
  const hasAbroadData = abroadSeries.some((value) => value !== null);
  // The comparison lines are always the whole model; say so when the main line is narrowed down.
  const narrowed = generation !== null || generations.length > 0;

  const chartData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [
        {
          label: 'Average price',
          // Converting the raw BGN values (rather than just relabeling axis ticks) so Chart.js's
          // auto-generated y-axis ticks land on round EUR numbers, not odd BGN-derived fractions.
          data: series.values.map(bgnToEur),
          borderColor: LINE_COLOR,
          backgroundColor: FILL_COLOR,
          pointBackgroundColor: LINE_COLOR,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          tension: 0.3,
          fill: true,
        },
        // No fill on these, so multiple country lines don't produce overlapping shaded regions.
        ...(hasAbroadData
          ? [
              {
                label: narrowed ? 'Abroad (all countries, whole model)' : 'Abroad (all countries)',
                data: abroadSeries.map((value) => (value === null ? null : bgnToEur(value))),
                borderColor: ABROAD_COLOR,
                pointBackgroundColor: ABROAD_COLOR,
                pointRadius: 2,
                pointHoverRadius: 4,
                borderWidth: 2,
                borderDash: [6, 3],
                tension: 0.3,
                fill: false,
                spanGaps: true,
              },
            ]
          : []),
        ...countries.map((country) => {
          const color = labelToColor(country);
          return {
            label: narrowed ? `${country} (whole model)` : country,
            data: buildCountryChartSeries(data, country).map((value) => (value === null ? null : bgnToEur(value))),
            borderColor: color,
            pointBackgroundColor: color,
            pointRadius: 2,
            pointHoverRadius: 4,
            borderWidth: 2,
            tension: 0.3,
            fill: false,
            spanGaps: true,
          };
        }),
      ],
    }),
    [series, abroadSeries, hasAbroadData, data, countries, narrowed],
  );

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: hasAbroadData || countries.length > 0 },
        tooltip: {
          callbacks: {
            label: (context) => `${Math.round(context.parsed.y).toLocaleString()} €`,
          },
        },
      },
      scales: {
        y: {
          ticks: { callback: (value) => Number(value).toLocaleString() },
          grid: { color: '#eef0f2' },
        },
        x: {
          grid: { display: false },
        },
      },
    }),
    [hasAbroadData, countries.length],
  );

  return (
    <Box sx={{ position: 'relative', width: '100%', height: { xs: 240, sm: 320 } }}>
      <Line options={options} data={chartData} />
    </Box>
  );
}
